/**
 * Catalogue des actes sensibles.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CE QUE CE FICHIER GARANTIT — ET CE QU'IL NE GARANTIT PAS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Le §2 demande « un helper qui garantit qu'une action sensible est tracee ». Il faut
 * etre precis sur le mot « garantit », parce qu'un client ne peut pas garantir cela.
 *
 * **Ce que le SERVEUR garantit** : l'ecriture au journal d'audit. Les endpoints
 * `/v1/admin/*` journalisent eux-memes, dans le meme aller-retour que l'acte. Un appel
 * direct par `curl` laisse exactement la meme trace qu'un clic ici. Si ce fichier
 * disparaissait, la tracabilite tiendrait toujours. C'est le seul modele defendable :
 * une garantie qui repose sur le client n'est pas une garantie, c'est une convention.
 *
 * **Ce que ce FICHIER garantit** : que l'operateur ne declenche pas un acte sensible
 * sans avoir vu ce qu'il fait et fourni ce que le serveur exigera. Concretement :
 *   - le motif est saisi AVANT l'appel, donc l'acte ne part pas pour revenir en 400 ;
 *   - la double confirmation exigee au §3 est appliquee uniformement, sans que chaque
 *     ecran ait a y penser ;
 *   - l'interface annonce a l'operateur ce qui sera consigne, avant qu'il valide.
 *
 * Autrement dit : le serveur empeche l'acte non trace, ce catalogue empeche l'acte
 * irreflechi. Les deux sont necessaires ; aucun ne remplace l'autre.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export interface ActeSensible {
  /** Identifiant technique, aligne sur `ActionsAudit` cote back. */
  readonly action: string;

  /** Ce que l'operateur lit dans la boite de confirmation. */
  readonly libelle: string;

  /** Consequence concrete, en une phrase. Pas de formule vague. */
  readonly consequence: string;

  /** Un motif est-il exige ? Le serveur refuse l'acte sans lui. */
  readonly motifRequis: boolean;

  /**
   * L'acte est-il reversible, et par quel geste ?
   *
   * `null` = irreversible. Le §3 impose de documenter la reversibilite quand elle
   * existe ; le champ est obligatoire pour forcer a se poser la question a chaque
   * ajout, plutot que de laisser le doute.
   */
  readonly reversibilite: string | null;

  /** Exige de retaper un mot de confirmation (§3 : actes destructeurs). */
  readonly confirmationRenforcee: boolean;

  /** L'acte expose ou detruit des donnees personnelles. */
  readonly donneesPersonnelles: boolean;
}

export const ACTES = {
  entrepriseSuspendue: {
    action: 'admin.entreprise.suspendue',
    libelle: 'Suspendre cette entreprise',
    consequence:
      'Ses membres perdent l’acces a l’application. Les donnees et les documents sont conserves intacts.',
    motifRequis: true,
    reversibilite: 'Reversible a tout moment par « Reactiver ».',
    confirmationRenforcee: false,
    donneesPersonnelles: false,
  },

  entrepriseReactivee: {
    action: 'admin.entreprise.reactivee',
    libelle: 'Reactiver cette entreprise',
    consequence: 'Ses membres retrouvent l’acces immediatement.',
    motifRequis: false,
    reversibilite: 'Reversible par une nouvelle suspension.',
    confirmationRenforcee: false,
    donneesPersonnelles: false,
  },

  utilisateurSuspendu: {
    action: 'admin.utilisateur.suspendu',
    libelle: 'Suspendre ce compte',
    consequence:
      'Le compte ne peut plus se connecter et ses sessions ouvertes sont fermees immediatement.',
    motifRequis: true,
    reversibilite: 'Reversible par « Reactiver ». Le compte n’est pas supprime.',
    confirmationRenforcee: false,
    donneesPersonnelles: false,
  },

  utilisateurReactive: {
    action: 'admin.utilisateur.reactive',
    libelle: 'Reactiver ce compte',
    consequence: 'Le titulaire peut de nouveau se connecter.',
    motifRequis: false,
    reversibilite: 'Reversible par une nouvelle suspension.',
    confirmationRenforcee: false,
    donneesPersonnelles: false,
  },

  reinitialisationDeclenchee: {
    action: 'admin.utilisateur.reinitialisation_declenchee',
    libelle: 'Declencher une reinitialisation de mot de passe',
    consequence:
      'Un lien de reinitialisation est destine au titulaire du compte. Vous ne le verrez pas : ni le lien, ni le mot de passe.',
    motifRequis: false,
    reversibilite: 'Sans effet tant que le titulaire n’utilise pas le lien.',
    confirmationRenforcee: false,
    donneesPersonnelles: false,
  },

  /*
    LES DEUX ACTES LES PLUS LOURDS DE L'OUTIL.

    Effacement RGPD : irreversible par nature — c'est meme le but. Confirmation
    renforcee obligatoire.

    Consultation de donnees personnelles : ce n'est pas destructeur, mais c'est
    precisement le geste que le §0.3 designe comme le danger principal. Il exige un
    motif, au meme titre qu'une suspension.
  */

  rgpdEffacementExecute: {
    action: 'admin.rgpd.effacement_execute',
    libelle: 'Executer l’effacement des donnees',
    consequence:
      'Les donnees personnelles sont remplacees par des valeurs neutres, definitivement. Les factures deja emises sont conservees : la loi l’impose.',
    motifRequis: true,
    reversibilite: null,
    confirmationRenforcee: true,
    donneesPersonnelles: true,
  },

  rgpdDemandeRefusee: {
    action: 'admin.rgpd.demande_refusee',
    libelle: 'Refuser cette demande',
    consequence: 'La demande est close. Le motif est conserve et opposable.',
    motifRequis: true,
    reversibilite: null,
    confirmationRenforcee: false,
    donneesPersonnelles: false,
  },

  rgpdExportProduit: {
    action: 'admin.rgpd.export_produit',
    libelle: 'Produire l’export des donnees',
    consequence:
      'Une archive des donnees de l’entreprise est generee. Elle contient des donnees personnelles.',
    motifRequis: true,
    reversibilite: 'L’archive peut etre detruite ; sa production reste consignee.',
    confirmationRenforcee: false,
    donneesPersonnelles: true,
  },

  couponSupprime: {
    action: 'admin.coupon.supprime',
    libelle: 'Desactiver ce coupon',
    consequence: 'Le code cesse d’etre utilisable. Les abonnements deja remises ne changent pas.',
    motifRequis: false,
    reversibilite: null,
    confirmationRenforcee: false,
    donneesPersonnelles: false,
  },
} as const satisfies Record<string, ActeSensible>;

export type CleActe = keyof typeof ACTES;
